import type { FixtureDescriptor, FixtureValues, RigSnapshot } from './types';

export type Point2 = [number, number];
export type Point3 = [number, number, number];

export interface SchematicElement {
  deviceId: string;
  elementIndex: number;
  /** This /3 placement uid was not present in the rig; its geometry remains drawable. */
  unresolved?: boolean;
  groupId?: string;
  /** The DATA run — one wiring fragment of a continuous piece of tape. Internal, never shown. */
  runId?: string;
  point: Point2;
  distanceM?: number;
  /** Position 0…N-1 along the authored room path. Additive: absent on older rig versions. */
  pathIndex?: number;
}

export interface SchematicGroup {
  id: string;
  zone?: string;
  order?: number;
  bounds?: { min: Point2; max: Point2 };
}

export interface SchematicRun {
  id: string;
  label?: string;
  description?: string;
  groupId?: string;
  zone?: string;
  points: Point2[];
}

/**
 * Which way along a data run the room path travels. `reverse` means the path enters at the fragment's
 * LAST element, so element indices decrease as you walk.
 */
export type TraversalDirection = 'forward' | 'reverse';

export interface SchematicContinuousRunMember {
  /** The data run — a wiring fragment id from `runs[]`. */
  run: string;
  direction: TraversalDirection;
}

/**
 * A CONTINUOUS RUN: the ordered chain of data runs whose ends physically MEET — one unbroken piece of
 * tape as a person in the room sees it, and the only run an operator is ever shown.
 *
 * Alchemy has four (three shelves and the bench) built out of several long-arm and short-arm data
 * fragments. The chain exists because a shelf is fed from the middle, so the data runs alone say nothing
 * about which way the tape continues.
 */
export interface SchematicContinuousRun {
  id: string;
  name?: string;
  members: SchematicContinuousRunMember[];
  elementCount?: number;
}

/** The authored room path: the continuous runs in the order a person walks them. */
export interface SchematicTraversal {
  order?: string[];
}

export interface SchematicLayout {
  schema: string;
  roomId?: string;
  /** Content digest shared with output-view/1 so address order can never be guessed. */
  layoutDigest?: string;
  bounds?: { min: Point2; max: Point2 };
  groups?: SchematicGroup[];
  /** The WIRING fragments. Addressing and drawing geometry only — never an operator-facing unit. */
  runs?: SchematicRun[];
  /** Additive: absent on rig versions published before the builder emitted them. */
  continuousRuns?: SchematicContinuousRun[];
  traversal?: SchematicTraversal;
  elements: SchematicElement[];
}

export const SCHEMATIC_DRAWING_SCHEMAS = new Set(['schematic-layout/3']);

export interface PanelElement extends Omit<SchematicElement, 'deviceId'> {
  key: string;
  /**
   * The controller target this pixel addresses, present only once the drawing key resolved. An awaiting or
   * unresolved pixel has none, so no lookup can mistake its drawing key for a wire address.
   */
  deviceId?: string;
  /** A /3 placement is waiting for the rig; its drawing and selection key are already known. */
  awaitingRig?: boolean;
  /** No rig fixture has this /3 placement uid; draw it, but do not address a controller. */
  unresolved?: boolean;
  groupId: string;
  /** The CONTINUOUS run this pixel belongs to — the unit every operator-facing count and label uses. */
  runId: string;
  /** The wiring fragment it is addressed through. Internal; kept for diagnosis, never displayed. */
  dataRunId: string;
  runName: string;
  wiringNote: string;
  /** Position along the CONTINUOUS run as the room is WALKED, not as it is wired. */
  runElementIndex: number;
  /** Position along the whole room path 0…n-1. `deviceId` + `elementIndex` remain the wire address. */
  stripIndex: number;
}

/** One wiring fragment of a continuous run, as a polyline oriented the way the path walks it. */
export interface PanelRunSegment {
  dataRunId: string;
  direction: TraversalDirection;
  points: Point2[];
}

/**
 * A continuous run, ready to draw and to select: the operator-facing unit. In a layout that publishes no
 * `continuousRuns`, each data run stands alone as one — exactly the previous model.
 */
export interface PanelRun {
  id: string;
  name: string;
  wiringNote: string;
  groupId: string;
  /** Its wiring fragments in path order, each oriented so `points[0]` is where the path enters it. */
  segments: PanelRunSegment[];
  /** Its pixels in SPATIAL order along the room path, never in wiring order. */
  elements: PanelElement[];
}

export interface PanelZone {
  id: string;
  name: string;
  order: number;
  runs: PanelRun[];
  elements: PanelElement[];
}

export interface PanelModel {
  layout: SchematicLayout;
  runs: PanelRun[];
  zones: PanelZone[];
  elements: PanelElement[];
  byKey: Map<string, PanelElement>;
  byAddress: Map<string, PanelElement>;
}

export interface GradientStop {
  id: string;
  position: number;
  level: number;
}

export interface LevelChange {
  label: string;
  before: Map<string, number>;
  after: Map<string, number>;
  /**
   * Gestures that belong to ONE authoring session share a merge key, so a whole gradient-editing
   * session (drag a stop, retype a level, flip mirror) collapses into a single undo step whose
   * `before` is the state the session started from.
   */
  mergeKey?: string;
}

export interface ElementLevelBatch {
  level: number;
  elements: Record<string, number[]>;
}

const LEVEL_EPSILON = 0.000_001;

export function clampLevel(value: number): number {
  return Math.max(0, Math.min(1, Number.isFinite(value) ? value : 0));
}

/**
 * How many addressable elements one fixture has — one rule, in one place.
 *
 * The descriptor may say so three different ways depending on which projection built it (`elementCount`
 * from `rig.ts`, a materialised `elements` array, or the run topology), and a conventional fixture with
 * none of them is one element rather than none. This was written out three times before an intent slot
 * needed the same number to size a selection mask; three copies of a fallback chain is how two of them
 * end up disagreeing about a fixture shape only one of them has met.
 */
export function fixtureElementCount(fixture: FixtureDescriptor): number {
  return Math.max(1, fixture.elementCount ?? fixture.elements?.length ?? fixture.elementTopology?.count ?? 1);
}

export function elementAddress(deviceId: string, elementIndex: number): string {
  return `${deviceId}:${elementIndex}`;
}

export interface ResolvedSchematicElement {
  element: SchematicElement;
  deviceId: string | undefined;
  /** A `/3` pixel the cloud table resolves to a target the controller's rig version does not have yet. */
  awaitingRig: boolean;
}

/**
 * The `/3` join table: lower-case MVR placement uid → sealed target id (the wire address), stamped with the
 * rig version it was read from.
 *
 * Only the cloud projection of a rig version (`projectRigDocument`) carries `placementUid`; the controller's
 * compact `get_rig` does not. So the console always builds this table from the cloud projection of the
 * room's active rig version, even while a direct link supplies the rest of the rig (CONTRACT-console S41).
 * The version stamp is what lets the resolver notice a controller still running another version (S42).
 */
export interface PlacementTargets {
  readonly rigVersionId: string | undefined;
  readonly byPlacement: ReadonlyMap<string, string>;
}

/** The join table one rig projection states, or none while that projection is not loaded. */
export function placementTargets(projection: RigSnapshot | undefined): PlacementTargets | undefined {
  if (!projection) return undefined;
  return {
    rigVersionId: projection.rigVersionId,
    byPlacement: new Map(projection.fixtures.filter((fixture) => fixture.placementUid)
      .map((fixture) => [fixture.placementUid!.toLowerCase(), fixture.deviceId])),
  };
}

/**
 * Published /3 drawings name MVR placements and resolve only through `placements`; fixture-derived layouts
 * name target ids and resolve against `rig`.
 *
 * When `rig` states a different rig version from the table (a controller that has not applied the cloud's
 * active version yet), a `/3` target the controller's rig lacks is awaiting, never a wire address
 * (CONTRACT-console S42). Same-version targets need no check: the table and the rig are the same rig.
 */
export function resolveSchematicElements(layout: SchematicLayout, rig: RigSnapshot | undefined,
  placements: PlacementTargets | undefined): ResolvedSchematicElement[] {
  const targets = new Set((rig?.fixtures ?? []).map((fixture) => fixture.deviceId));
  const versionSkew = Boolean(rig?.rigVersionId && placements?.rigVersionId
    && rig.rigVersionId !== placements.rigVersionId);
  return layout.elements.map((element) => {
    if (layout.schema === 'schematic-layout/3') {
      const target = placements?.byPlacement.get(element.deviceId.toLowerCase());
      const awaitingRig = versionSkew && target !== undefined && !targets.has(target);
      return { element, deviceId: awaitingRig ? undefined : target, awaitingRig };
    }
    const deviceId = layout.schema === 'fixture-position-v1' && targets.has(element.deviceId)
      ? element.deviceId : undefined;
    return { element, deviceId, awaitingRig: false };
  });
}

/**
 * The drawing's elements in the order of the controller's wire map: the rig's own fixture order, then element
 * index. The controller enumerates the fixtures it loaded, in load order: Firestore `devices[]` order for a
 * schema-1 rig version, sorted target ids for a published `nuance.rig/1`. `rig.fixtures` already carries that
 * order on both paths (the cloud projection orders by the same rule, and a direct link's `get_rig` is the
 * controller's own list), so the pairing never sorts by id (CONTRACT-console B95).
 *
 * A target the rig does not list has no wire position, so it is returned unresolved and the view blanks
 * rather than guessing.
 */
export function wireOrderedSchematicElements(layout: SchematicLayout, rig: RigSnapshot | undefined,
  placements: PlacementTargets | undefined): ResolvedSchematicElement[] {
  const fixtureRank = new Map((rig?.fixtures ?? []).map((fixture, index) => [fixture.deviceId.toLowerCase(), index]));
  const ranked = resolveSchematicElements(layout, rig, placements).map((resolved) => {
    const rank = resolved.deviceId === undefined ? undefined : fixtureRank.get(resolved.deviceId.toLowerCase());
    return { resolved: rank === undefined ? { ...resolved, deviceId: undefined } : resolved, rank: rank ?? -1 };
  });
  return ranked.sort((left, right) => left.rank - right.rank
    || left.resolved.element.elementIndex - right.resolved.element.elementIndex)
    .map(({ resolved }) => resolved);
}

const warnedPlacements = new WeakSet<SchematicLayout>();

/**
 * Legacy rig projections embedded a wiring note after an em dash in the fixture name. Newer projections
 * carry it in `description`; keeping this parser lets both shapes resolve to the same operator-facing run.
 */
export function splitRunLabel(label: string | undefined, fallback: string): { name: string; wiringNote: string } {
  const value = label?.trim() || fallback;
  const separator = value.indexOf(' — ');
  if (separator < 0) return { name: value, wiringNote: '' };
  return {
    name: value.slice(0, separator).trim() || fallback,
    wiringNote: value.slice(separator + 3).trim(),
  };
}

function titleCase(value: string): string {
  return value
    .replace(/[._-]+/g, ' ')
    .replace(/\b\w/g, (character) => character.toUpperCase());
}

function fallbackLayout(rig: RigSnapshot): SchematicLayout | undefined {
  const fixtures = rig.fixtures.filter((fixture) => fixture.isControllable !== false);
  if (!fixtures.length) return undefined;
  const fixtureRuns = new Map<string, typeof fixtures>();
  fixtures.forEach((fixture, index) => {
    const name = splitRunLabel(fixture.name, `Run ${index + 1}`).name;
    fixtureRuns.set(name, [...(fixtureRuns.get(name) ?? []), fixture]);
  });
  const physicalRuns = [...fixtureRuns];
  const columns = Math.max(1, Math.ceil(Math.sqrt(physicalRuns.length)));
  const elements: SchematicElement[] = [];
  const runs: SchematicRun[] = [];
  const groups = new Map<string, SchematicGroup>();
  let pathIndex = 0;
  physicalRuns.forEach(([name, fragments], runIndex) => {
    const counts = fragments.map(fixtureElementCount);
    const count = counts.reduce((total, value) => total + value, 0);
    const positioned = fragments.find((fixture) => fixture.position)?.position;
    const origin: Point2 = positioned
      ? [positioned.x, positioned.y]
      : [runIndex % columns, Math.floor(runIndex / columns)];
    const runId = `fallback-run:${name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
    const zone = name.replace(/\s+run\s+[^ ]+.*$/i, '').trim() || 'Room';
    const groupId = `fallback-zone:${zone.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
    const points: Point2[] = [origin, [origin[0] + Math.max(.03, count * .003), origin[1]]];
    const description = [...new Set(fragments.flatMap((fixture) => {
      if (typeof fixture.description === 'string' && fixture.description.trim()) return [fixture.description.trim()];
      const parsed = splitRunLabel(fixture.name, '');
      return parsed.wiringNote ? [parsed.wiringNote] : [];
    }))].join(' · ');
    runs.push({ id: runId, label: name, ...(description ? { description } : {}), groupId, points });
    groups.set(groupId, { id: groupId, zone, order: groups.size });
    let runElementIndex = 0;
    fragments.forEach((fixture, fragmentIndex) => {
      for (let elementIndex = 0; elementIndex < counts[fragmentIndex]; elementIndex += 1) {
        const progress = count === 1 ? 0 : runElementIndex / (count - 1);
        elements.push({
          deviceId: fixture.deviceId,
          elementIndex,
          groupId,
          runId,
          point: [origin[0] + (points[1][0] - origin[0]) * progress, origin[1]],
          distanceM: runElementIndex,
          pathIndex: pathIndex++,
        });
        runElementIndex += 1;
      }
    });
  });
  const xs = elements.map((element) => element.point[0]);
  const ys = elements.map((element) => element.point[1]);
  const pad = Math.max(.2, Math.max(Math.max(...xs) - Math.min(...xs), Math.max(...ys) - Math.min(...ys)) * .04);
  return {
    schema: 'fixture-position-v1',
    bounds: {
      min: [Math.min(...xs) - pad, Math.min(...ys) - pad],
      max: [Math.max(...xs) + pad, Math.max(...ys) + pad],
    },
    groups: [...groups.values()],
    runs,
    // This layout invents its own geometry from fixture positions — every run is one unbroken left-to-right
    // line laid out in data order — so its continuous runs and room path are known exactly rather than
    // missing, and the operator sees the same run count the rig reports.
    continuousRuns: runs.map((run) => ({
      id: run.id,
      name: run.label,
      members: [{ run: run.id, direction: 'forward' as const }],
      elementCount: elements.filter((element) => element.runId === run.id).length,
    })),
    traversal: { order: runs.map((run) => run.id) },
    elements,
  };
}

export function fallbackPanelLayout(rig?: RigSnapshot): SchematicLayout | undefined {
  return rig ? fallbackLayout(rig) : undefined;
}

/**
 * The room summary uses a resolved PanelModel for its run count. A RigSnapshot only knows wiring
 * fixtures, so it may supply the pixel total while the published room path is still loading, but it must
 * never be mistaken for a count of continuous runs.
 */
export function roomRigCounts(model?: PanelModel, rig?: RigSnapshot): { runs: number | undefined; pixels: number } {
  const fixtures = rig?.fixtures.filter((fixture) => fixture.isControllable !== false) ?? [];
  return {
    runs: model?.runs.length,
    pixels: model?.elements.length ?? fixtures.reduce(
      (total, fixture) => total + fixtureElementCount(fixture),
      0,
    ),
  };
}

export function runCountLabel(count: number | undefined): string {
  return count == null ? '— runs' : `${count.toLocaleString()} run${count === 1 ? '' : 's'}`;
}

/**
 * The `schematic-layout` schemas a controller will accept as an effect's `roomPath`.
 *
 * Source of truth: `SchematicPath.Validate` in `nuance-venue-controller/internal/live/effect/roomwalk.go`,
 * which rejects every other schema outright. This console draws older schematics happily — the drawing
 * only needs `elements` — so the versions it can render and those it may SEND are different facts.
 */
export const CONTROLLER_ROOM_PATH_SCHEMAS = new Set(['schematic-layout/2', 'schematic-layout/3']);

/**
 * The `roomPath` clause of a start command, present only when the controller can read the document.
 *
 * `roomPath` is an ENRICHMENT: it tells a path-travelling effect the route the rig author drew, and a
 * controller given none falls back to `packageGeometricWalk`, inferring the walk from geometry. But
 * `SchematicPath.Validate` refuses a schema it does not know, and the start handler surfaces that refusal
 * as a failed command — so a room still on `schematic-layout/1` could not start an effect at all, because
 * of an optional extra. Omitting the clause hands that room the documented fallback instead of a dead
 * Start button.
 *
 * Callers spread the result, so there is exactly one place that decides, and no call site names a schema.
 */
export function effectRoomPathParams(layout: SchematicLayout | undefined): { roomPath?: SchematicLayout } {
  return layout && CONTROLLER_ROOM_PATH_SCHEMAS.has(layout.schema) ? { roomPath: layout } : {};
}

/** True only for continuous runs authored into a published schematic, never fixture-derived fallback. */
export function hasAuthoredContinuousRuns(layout?: SchematicLayout): boolean {
  return /^schematic-layout\/\d+$/.test(layout?.schema ?? '')
    && Boolean(layout?.continuousRuns?.some((run) => run.id && run.members?.length));
}

/** Omit the run clause until an authored continuous-run path is available. */
export function roomRigSummary(counts: { runs: number | undefined; pixels: number }): string {
  const pixels = `${counts.pixels.toLocaleString()} pixel${counts.pixels === 1 ? '' : 's'}`;
  return counts.runs == null ? pixels : `${runCountLabel(counts.runs)} · ${pixels}`;
}

/** One console warning per layout document, so a drawing without a room path is diagnosable, not noisy. */
const warnedLayouts = new WeakSet<SchematicLayout>();

function dataRunIdOf(element: SchematicElement): string {
  return element.runId || `device:${element.deviceId}`;
}

/** The chain of wiring fragments that makes up one continuous piece of tape. */
interface RunChain {
  id: string;
  label?: string;
  members: SchematicContinuousRunMember[];
}

/**
 * The CONTINUOUS runs and the room path, as the rig authored them.
 *
 * This is the whole reason the layout carries `continuousRuns`, `traversal` and `pathIndex`. The tape's
 * data runs are a wiring fact — a shelf is fed in the middle, so its long and short arms may run in opposite directions
 * and each is a separate fragment. Neither the operator's idea of a "run" nor the direction a gradient
 * should sweep can be recovered from the fragments alone. So:
 *
 * - `continuousRuns[].members` chains the fragments whose ends physically meet, each with the direction
 *   the path walks it, and that CHAIN is the run an operator sees, counts, names and hovers.
 * - `traversal.order` walks those continuous runs the way a person walks the room.
 * - `pathIndex` states each pixel's place along that walk, and wins over the chain construction when both
 *   are present, because it is the rig's explicit statement of the path.
 *
 * Only the element→wire mapping (`deviceId` + `elementIndex`, carried untouched on every PanelElement)
 * follows the wiring.
 *
 * A layout missing any of it — a rig version finalized before the builder emitted these — keeps exactly
 * the previous behaviour: every data run is its own run, in authored order and wiring direction. That is
 * said once on the console and never as a UI error: a gradient that reads backwards on one long arm is a
 * far smaller failure than a room that will not draw. Drift is handled the same way — fragments no chain
 * claims become their own runs, chains and order entries naming unknown ids are dropped, and the console
 * names what it fell back on.
 */
function resolveRoomPath(layout: SchematicLayout, rawRuns: readonly SchematicRun[]): {
  chains: RunChain[];
  order: Map<string, number>;
  pathAuthored: boolean;
} {
  const dataRunIds = new Set(rawRuns.map((run) => run.id));
  const declared = (layout.continuousRuns ?? []).filter((run): run is SchematicContinuousRun => Boolean(run?.id));
  const problems: string[] = [];
  const chains: RunChain[] = [];
  const claimed = new Set<string>();
  for (const run of declared) {
    const members = (run.members ?? [])
      .filter((member) => typeof member?.run === 'string' && dataRunIds.has(member.run) && !claimed.has(member.run))
      .map((member) => ({ run: member.run, direction: member.direction === 'reverse' ? 'reverse' as const : 'forward' as const }));
    if (!members.length) continue;
    for (const member of members) claimed.add(member.run);
    chains.push({ id: run.id, label: run.name, members });
  }
  if (declared.length && chains.length !== declared.length) {
    problems.push(`${declared.length - chains.length} of its continuous runs name no fragment this drawing contains`);
  }
  // Nothing may be dropped: a fragment no chain claims stands alone, exactly as it did before chains.
  const orphans = rawRuns.filter((run) => !claimed.has(run.id));
  if (!declared.length) problems.push('it publishes no continuous runs, so each wiring fragment is shown as its own run');
  else if (orphans.length) problems.push(`${orphans.length} wiring fragment(s) belong to no continuous run (${orphans.slice(0, 4).map((run) => run.id).join(', ')})`);
  for (const run of orphans) chains.push({ id: run.id, label: run.label, members: [{ run: run.id, direction: 'forward' }] });

  const chainIds = new Set(chains.map((chain) => chain.id));
  const listed = (layout.traversal?.order ?? []).filter((id) => typeof id === 'string');
  const walk = [...new Set(listed.filter((id) => chainIds.has(id)))];
  if (!listed.length) problems.push('it publishes no room path order for those runs');
  else {
    const foreign = [...new Set(listed.filter((id) => !chainIds.has(id)))];
    if (foreign.length) problems.push(`its room path names ${foreign.length} run(s) that do not exist (${foreign.slice(0, 4).join(', ')})`);
    const forgotten = chains.filter((chain) => !walk.includes(chain.id));
    if (forgotten.length) problems.push(`its room path leaves out ${forgotten.length} run(s) (${forgotten.slice(0, 4).map((chain) => chain.id).join(', ')})`);
  }
  const order = new Map<string, number>();
  for (const [index, id] of [...walk, ...chains.map((chain) => chain.id).filter((id) => !walk.includes(id))].entries()) {
    order.set(id, index);
  }

  const pathAuthored = layout.elements.every((element) => Number.isInteger(element.pathIndex));
  if (!pathAuthored) problems.push('its pixels carry no path index');

  if (problems.length && !warnedLayouts.has(layout)) {
    warnedLayouts.add(layout);
    console.warn(
      `[room] this rig version does not fully describe the path through the room — ${problems.join('; ')}. `
      + 'Falling back to the drawing\'s own fragment order and wiring direction, so a gradient or pattern '
      + 'crossing tape that is fed from opposite ends will read reversed there until the rig republishes.',
    );
  }
  return { chains, order, pathAuthored };
}

/** The fragment's pixels in WIRING order: by distance along the tape when it is stated, else as authored. */
function dataRunOrder<T extends { distanceM?: number }>(elements: readonly T[]): T[] {
  if (!elements.every((element) => Number.isFinite(element.distanceM))) return [...elements];
  return [...elements].sort((left, right) => left.distanceM! - right.distanceM!);
}

/** A drawing pixel after resolution, before it is placed on its continuous run. */
type DrawingElement = Omit<SchematicElement, 'deviceId'> & Pick<PanelElement, 'deviceId' | 'awaitingRig'>;

export function buildPanelModel(layout: SchematicLayout, rig: RigSnapshot | undefined,
  placements: PlacementTargets | undefined): PanelModel {
  const resolved = resolveSchematicElements(layout, rig, placements);
  const waitingForRig = layout.schema === 'schematic-layout/3' && !placements;
  const drawingElements = resolved.map(({ element, deviceId, awaitingRig }): DrawingElement => ({
    ...element, deviceId,
    ...(deviceId ? {} : waitingForRig || awaitingRig ? { awaitingRig: true } : { unresolved: true }),
  }));
  // Selection keys and wiring fragments come from the DRAWING's own key, which every pixel has whether or not
  // it resolved; the resolved target is only the wire address.
  const selectionKeys = new Map<DrawingElement, string>(drawingElements.map((element, index) => [
    element, elementAddress(resolved[index].element.deviceId, element.elementIndex),
  ]));
  const dataRunIds = new Map<DrawingElement, string>(drawingElements.map((element, index) => [
    element, dataRunIdOf(resolved[index].element),
  ]));
  const dataRunIdOfDrawn = (element: DrawingElement): string => dataRunIds.get(element)!;
  const drawing = { ...layout, elements: drawingElements };
  if (!waitingForRig && resolved.some(({ deviceId, awaitingRig }) => !deviceId && !awaitingRig)
    && !warnedPlacements.has(layout)) {
    warnedPlacements.add(layout);
    console.warn('Schematic placement has no matching get_rig device.');
  }
  const rawRuns: SchematicRun[] = drawing.runs?.length
    ? drawing.runs
    : Array.from(new Set(drawing.elements.map(dataRunIdOfDrawn))).map((id) => ({
      id,
      label: id,
      groupId: drawing.elements.find((element) => dataRunIdOfDrawn(element) === id)?.groupId || 'room',
      points: drawing.elements.filter((element) => dataRunIdOfDrawn(element) === id).map((element) => element.point),
    }));
  const rawRunById = new Map(rawRuns.map((run) => [run.id, run]));
  const fragments = new Map<string, DrawingElement[]>();
  for (const element of drawing.elements) {
    const id = dataRunIdOfDrawn(element);
    fragments.set(id, [...(fragments.get(id) ?? []), element]);
  }
  for (const [id, elements] of fragments) fragments.set(id, dataRunOrder(elements));

  const { chains, order, pathAuthored } = resolveRoomPath(layout, rawRuns);
  const runs = chains.map((chain, index): PanelRun => {
    const parsed = splitRunLabel(chain.label, `Run ${index + 1}`);
    const descriptions = [...new Set(chain.members.flatMap((member) => {
      const description = rawRunById.get(member.run)?.description?.trim();
      return description ? [description] : [];
    }))];
    const name = parsed.name;
    const wiringNote = parsed.wiringNote || descriptions.join(' · ');
    const segments = chain.members.map((member): PanelRunSegment => {
      const points = rawRunById.get(member.run)?.points ?? [];
      return {
        dataRunId: member.run,
        direction: member.direction,
        // Oriented so the polyline starts where the path enters this fragment, and the chain draws as
        // one stroke from the room's entry point to its far end.
        points: member.direction === 'reverse' ? [...points].reverse() : points,
      };
    }).filter((segment) => segment.points.length > 0);
    const chained = chain.members.flatMap((member) => {
      const elements = fragments.get(member.run) ?? [];
      return member.direction === 'reverse' ? [...elements].reverse() : elements;
    });
    const walked = pathAuthored ? [...chained].sort((left, right) => left.pathIndex! - right.pathIndex!) : chained;
    const groupId = rawRunById.get(chain.members[0].run)?.groupId
      || rawRunById.get(chain.members[0].run)?.zone
      || walked[0]?.groupId
      || 'room';
    const elements = walked.map((raw, runElementIndex): PanelElement => ({
      ...raw,
      key: selectionKeys.get(raw)!,
      groupId,
      runId: chain.id,
      dataRunId: dataRunIdOfDrawn(raw),
      runName: name,
      wiringNote,
      runElementIndex,
      stripIndex: 0,
    }));
    return { id: chain.id, name, wiringNote, groupId, segments, elements };
  }).filter((run) => run.elements.length > 0);
  runs.sort((left, right) => (order.get(left.id) ?? 0) - (order.get(right.id) ?? 0));
  // The room path is the runs walked in order; `stripIndex` is a pixel's place along it.
  const elements = runs.flatMap((run) => run.elements);
  elements.forEach((element, index) => { element.stripIndex = index; });
  const groupOrder = new Map((layout.groups ?? []).map((group, index) => [group.id, group.order ?? index]));
  const zoneIds = Array.from(new Set(runs.map((run) => run.groupId)));
  const zones = zoneIds.map((id, index): PanelZone => {
    const zoneRuns = runs.filter((run) => run.groupId === id);
    return {
      id,
      name: titleCase((layout.groups ?? []).find((group) => group.id === id)?.zone || id),
      // With an authored room path the areas read in the order they are walked; without one they keep the
      // drawing's own group order.
      order: layout.traversal?.order?.length ? index : (groupOrder.get(id) ?? index),
      runs: zoneRuns,
      elements: zoneRuns.flatMap((run) => run.elements),
    };
  }).sort((left, right) => left.order - right.order);
  const byKey = new Map(elements.map((element) => [element.key, element]));
  const byAddress = new Map(elements.flatMap((element) => element.deviceId
    ? [[elementAddress(element.deviceId, element.elementIndex), element] as const] : []));
  return { layout, runs, zones, elements, byKey, byAddress };
}

/**
 * A dimmer-class channel key. The rig's keys are `group[/subType]` (`intensity/dimmer`,
 * `intensity/dimmer_2` for the tape's parallel pair — `internal/rig/offsets.go` ChannelKey +
 * the importer's element-local disambiguation pass). Matching on the SUBTYPE is what keeps
 * `intensity/shutter` and `intensity/strobe` out of a level write.
 */
const DIMMER_CLASS = /^(intensity|dimmer)$|(^|\/)dimmer(_\d+)?$/i;

function channelKey(channel: { key?: string; group?: string; subType?: string }): string {
  return channel.key || [channel.group, channel.subType].filter(Boolean).join('/');
}

/**
 * EVERY dimmer-class channel an element exposes, in declaration order.
 *
 * This is deliberately plural. The room's tape profile (`pixel-strip-parallel-dimmer`) wires TWO
 * parallel 16-bit dimmer channels per element, and writing only the first leaves the second holding
 * its previous value — the pixel then sits at whatever the stale channel says instead of the level the
 * operator asked for (measured on the real Pi, 2026-09-12). So a level write always carries the same
 * value on every dimmer-class channel of the element, read off the rig descriptor's channel summary
 * rather than guessed. When the daemon grows a rule that fans one `dimmer` write out across an
 * element's parallel channels, this collapses back to one key and nothing else has to change.
 */
export function fixtureIntensityKeys(fixture?: FixtureDescriptor): string[] {
  const capability = (fixture as { capabilities?: { hasIntensity?: boolean } } | undefined)?.capabilities;
  if (capability?.hasIntensity === false) return [];
  const summaries = [
    fixture?.elements?.[0]?.channels,
    fixture?.representativeChannels,
    fixture?.channels,
  ].find((candidate) => candidate?.length) ?? [];
  const keys = summaries.map(channelKey).filter((key) => key && DIMMER_CLASS.test(key));
  const listed = (fixture?.elements?.[0]?.channelKeys ?? fixture?.channelKeys ?? []).filter((key) => DIMMER_CLASS.test(key));
  const all = [...new Set([...keys, ...listed])];
  if (all.length) return all;
  return capability?.hasIntensity ? ['intensity/dimmer'] : [];
}

/**
 * The channel a level is READ from for display. The parallel dimmer channels always hold the same
 * value once the console has written them, so the first one is the element's level.
 */
export function fixtureIntensityKey(fixture?: FixtureDescriptor): string | undefined {
  return fixtureIntensityKeys(fixture)[0];
}

export function liveElementLevel(
  fixture: FixtureValues | undefined,
  descriptor: FixtureDescriptor | undefined,
  elementIndex: number,
): number {
  const keys = fixtureIntensityKeys(descriptor);
  if (!keys.length) return 0;
  const element = fixture?.elements?.find((candidate) => candidate.index === elementIndex);
  for (const key of keys) {
    const direct = element?.values?.[key];
    if (direct != null) return clampLevel(direct);
  }
  for (const key of keys) {
    const fixtureDirect = fixture?.values?.[key];
    if (fixtureDirect != null) return clampLevel(fixtureDirect);
  }
  return 0;
}

export function selectedRunIds(selection: ReadonlySet<string>, model: PanelModel): string[] {
  const ids = new Set<string>();
  for (const element of model.elements) if (selection.has(element.key)) ids.add(element.runId);
  return model.runs.filter((run) => ids.has(run.id)).map((run) => run.id);
}

export function selectionSummary(selection: ReadonlySet<string>, model: PanelModel): {
  pixels: number;
  runs: PanelRun[];
  headline: string;
} {
  const selectedRuns = new Set(selectedRunIds(selection, model));
  const runs = model.runs.filter((run) => selectedRuns.has(run.id));
  const pixels = selection.size;
  return {
    pixels,
    runs,
    headline: pixels === 0
      ? 'No pixels selected'
      : `${pixels.toLocaleString()} pixel${pixels === 1 ? '' : 's'} across ${runs.length} run${runs.length === 1 ? '' : 's'}`,
  };
}

export function toElementTargets(keys: Iterable<string>, model: PanelModel): Record<string, number[]> {
  const targets = new Map<string, number[]>();
  for (const key of keys) {
    const element = model.byKey.get(key);
    if (!element?.deviceId) continue;
    targets.set(element.deviceId, [...(targets.get(element.deviceId) ?? []), element.elementIndex]);
  }
  return Object.fromEntries([...targets]
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([deviceId, indices]) => [deviceId, [...new Set(indices)].sort((left, right) => left - right)]));
}

export function summarizeLevels(values: Iterable<number>): { min: number; max: number; uniform: boolean; label: string } {
  const levels = [...values].map(clampLevel);
  const min = levels.length ? Math.min(...levels) : 0;
  const max = levels.length ? Math.max(...levels) : 0;
  const uniform = max - min <= LEVEL_EPSILON;
  const asPercent = (value: number) => `${Math.round(value * 100)}%`;
  return { min, max, uniform, label: uniform ? asPercent(max) : `${asPercent(min)}–${asPercent(max)}` };
}

export function selectionLevels(
  selection: ReadonlySet<string>,
  model: PanelModel,
  levels: ReadonlyMap<string, number>,
): number[] {
  return model.elements.filter((element) => selection.has(element.key)).map((element) => clampLevel(levels.get(element.key) ?? 0));
}

export function normalizeGradientStops(stops: readonly GradientStop[]): GradientStop[] {
  const deduped = new Map<string, GradientStop>();
  for (const stop of stops) deduped.set(stop.id, { ...stop, position: clampLevel(stop.position), level: clampLevel(stop.level) });
  const result = [...deduped.values()].sort((left, right) => left.position - right.position || left.id.localeCompare(right.id));
  if (!result.length) return [
    { id: 'a', position: 0, level: 0 },
    { id: 'b', position: 1, level: 1 },
  ];
  if (result.length === 1) return [result[0], { id: `${result[0].id}-end`, position: 1, level: result[0].level }];
  return result;
}

export function addGradientStop(stops: readonly GradientStop[], position: number, id: string): GradientStop[] {
  const normalized = normalizeGradientStops(stops);
  return normalizeGradientStops([...normalized, { id, position: clampLevel(position), level: interpolateGradient(normalized, clampLevel(position)) }]);
}

export function moveGradientStop(
  stops: readonly GradientStop[],
  id: string,
  position: number,
  level?: number,
): GradientStop[] {
  return normalizeGradientStops(stops.map((stop) => stop.id === id
    ? { ...stop, position: clampLevel(position), level: level == null ? stop.level : clampLevel(level) }
    : stop));
}

export function removeGradientStop(stops: readonly GradientStop[], id: string): GradientStop[] {
  if (stops.length <= 2) return normalizeGradientStops(stops);
  return normalizeGradientStops(stops.filter((stop) => stop.id !== id));
}

export function interpolateGradient(stops: readonly GradientStop[], position: number): number {
  const normalized = normalizeGradientStops(stops);
  const t = clampLevel(position);
  if (t <= normalized[0].position) return normalized[0].level;
  if (t >= normalized[normalized.length - 1].position) return normalized[normalized.length - 1].level;
  const rightIndex = normalized.findIndex((stop) => stop.position >= t);
  const left = normalized[Math.max(0, rightIndex - 1)];
  const right = normalized[rightIndex];
  const distance = Math.max(Number.EPSILON, right.position - left.position);
  const progress = (t - left.position) / distance;
  return clampLevel(left.level + (right.level - left.level) * progress);
}

export function gradientLevels(stops: readonly GradientStop[], count: number, mirror = false): number[] {
  if (count <= 0) return [];
  if (count === 1) return [interpolateGradient(stops, 0)];
  return Array.from({ length: count }, (_, index) => {
    const raw = index / (count - 1);
    const position = mirror ? (raw <= .5 ? raw * 2 : (1 - raw) * 2) : raw;
    return interpolateGradient(stops, position);
  });
}

export const GRADIENT_PRESETS: Readonly<Record<string, GradientStop[]>> = {
  'fade-in': [
    { id: 'a', position: 0, level: 0 },
    { id: 'b', position: 1, level: 1 },
  ],
  'fade-out': [
    { id: 'a', position: 0, level: 1 },
    { id: 'b', position: 1, level: 0 },
  ],
  peak: [
    { id: 'a', position: 0, level: 0 },
    { id: 'peak', position: .5, level: 1 },
    { id: 'b', position: 1, level: 0 },
  ],
};

export function mapLevelsToSelection(
  selection: ReadonlySet<string>,
  model: PanelModel,
  levels: readonly number[],
): Map<string, number> {
  const ordered = model.elements.filter((element) => selection.has(element.key));
  return new Map(ordered.map((element, index) => [element.key, clampLevel(levels[index] ?? levels.at(-1) ?? 0)]));
}

export function nudgeLevels(
  selection: ReadonlySet<string>,
  model: PanelModel,
  current: ReadonlyMap<string, number>,
  offset: number,
): Map<string, number> {
  return new Map(model.elements
    .filter((element) => selection.has(element.key))
    .map((element) => [element.key, clampLevel((current.get(element.key) ?? 0) + offset)]));
}

export function groupElementLevelBatches(levels: ReadonlyMap<string, number>, model: PanelModel): ElementLevelBatch[] {
  const groups = new Map<string, { level: number; keys: string[] }>();
  for (const [key, raw] of levels) {
    if (!model.byKey.get(key)?.deviceId) continue;
    const level = clampLevel(raw);
    const bucket = level.toFixed(6);
    const group = groups.get(bucket) ?? { level, keys: [] };
    group.keys.push(key);
    groups.set(bucket, group);
  }
  return [...groups.values()]
    .sort((left, right) => left.level - right.level)
    .map((group) => ({ level: group.level, elements: toElementTargets(group.keys, model) }));
}

export function contiguousSelections(selection: ReadonlySet<string>, run: PanelRun): PanelElement[][] {
  const stretches: PanelElement[][] = [];
  let current: PanelElement[] = [];
  for (const element of run.elements) {
    if (selection.has(element.key)) current.push(element);
    else if (current.length) {
      stretches.push(current);
      current = [];
    }
  }
  if (current.length) stretches.push(current);
  return stretches;
}

/**
 * A brightness-distribution row: one per CONTINUOUS run, in ROOM PATH order.
 *
 * The gradient editor draws these under its stops bar so the operator can see where each run falls on the
 * axis they are editing. The gradient itself still APPLIES across the concatenated selection — that is
 * what "across the selection" means.
 */
export interface RunDistribution {
  runId: string;
  name: string;
  /** Pixels of this run that are selected. */
  selected: number;
  /** The run's full pixel count, for context. */
  total: number;
  /** The selected pixels' levels, in the direction the room path walks this run. */
  levels: number[];
  /** Where this run's selected pixels start in the concatenated selection order. */
  offset: number;
}

export function runDistributions(
  selection: ReadonlySet<string>,
  model: PanelModel,
  levels: ReadonlyMap<string, number>,
): RunDistribution[] {
  const rows: RunDistribution[] = [];
  let offset = 0;
  for (const run of model.runs) {
    const selected = run.elements.filter((element) => selection.has(element.key));
    if (!selected.length) continue;
    rows.push({
      runId: run.id,
      name: run.name,
      selected: selected.length,
      total: run.elements.length,
      levels: selected.map((element) => clampLevel(levels.get(element.key) ?? 0)),
      offset,
    });
    offset += selected.length;
  }
  return rows;
}

/**
 * The ROOM PATH: every pixel has one index 0…n-1 along the room, formed by walking the CONTINUOUS runs in
 * the rig's authored `traversal.order` and each one in the direction its chain states — the pixels'
 * `pathIndex` when the rig publishes it. `buildPanelModel` assigns that as `stripIndex`; it is always the
 * SPATIAL path, never the wiring order, and never a per-venue table.
 */
export function pathOrder(model: PanelModel): PanelElement[] {
  return model.elements;
}

/**
 * The contiguous block of LIT pixels that `key` belongs to, walking the room path in both directions
 * and stopping where the level reaches zero. Crosses run boundaries, because the path does.
 * Returns an empty set when the pixel itself is dark — there is no block to grab.
 */
export function litBlock(
  key: string,
  model: PanelModel,
  levels: ReadonlyMap<string, number>,
  threshold = 0.004,
): Set<string> {
  const element = model.byKey.get(key);
  if (!element) return new Set();
  const path = model.elements;
  const lit = (index: number) => {
    const candidate = path[index];
    return Boolean(candidate) && clampLevel(levels.get(candidate.key) ?? 0) > threshold;
  };
  if (!lit(element.stripIndex)) return new Set();
  let start = element.stripIndex;
  let end = element.stripIndex;
  while (lit(start - 1)) start -= 1;
  while (lit(end + 1)) end += 1;
  return new Set(path.slice(start, end + 1).map((candidate) => candidate.key));
}

export interface ProjectedPoint {
  key: string;
  x: number;
  y: number;
  visible: boolean;
}

/**
 * The pixel whose projected position is nearest the pointer, or undefined beyond `tolerance`.
 * Passing `Number.POSITIVE_INFINITY` makes it an unconditional nearest-on-path lookup, which is how a
 * pattern drag follows the pointer's projection onto the tape instead of raw screen x/y.
 */
export function nearestProjected(
  points: readonly ProjectedPoint[],
  x: number,
  y: number,
  tolerance = Number.POSITIVE_INFINITY,
): ProjectedPoint | undefined {
  let best: ProjectedPoint | undefined;
  let bestSquared = Number.POSITIVE_INFINITY;
  for (const point of points) {
    if (!point.visible) continue;
    const squared = (point.x - x) ** 2 + (point.y - y) ** 2;
    if (squared < bestSquared) {
      bestSquared = squared;
      best = point;
    }
  }
  return best && Math.sqrt(bestSquared) <= tolerance ? best : undefined;
}

/**
 * The glow around a lit pixel: radius and alpha both grow with level, so "on" is obvious next to
 * "off" at a glance and a 10% pixel still reads as lit. `gamma` below 1 lifts the low end so the first
 * few percent are visible. An element at zero gets no glow at all — just the dim tape line.
 */
export function glowRadius(level: number, coreRadius: number, gamma = 0.7): number {
  const value = clampLevel(level);
  if (value <= 0.002) return 0;
  return coreRadius + coreRadius * 4.2 * value ** gamma;
}

export function glowAlpha(level: number): number {
  const value = clampLevel(level);
  if (value <= 0.002) return 0;
  return 0.15 + 0.6 * value;
}

export class LevelHistory {
  private entries: LevelChange[] = [];
  private cursor = 0;

  constructor(private readonly capacity = 100) {}

  get canUndo(): boolean { return this.cursor > 0; }
  get canRedo(): boolean { return this.cursor < this.entries.length; }
  get length(): number { return this.entries.length; }

  push(change: LevelChange): void {
    if (!change.after.size) return;
    this.entries = this.entries.slice(0, this.cursor);
    const last = this.entries.at(-1);
    if (change.mergeKey && last?.mergeKey === change.mergeKey) {
      // Same authoring session: keep the session's original "before", take the newest "after", and
      // union in any pixel this step touched that earlier steps did not.
      for (const [key, value] of change.before) if (!last.before.has(key)) last.before.set(key, value);
      last.after = new Map([...last.after, ...change.after]);
      last.label = change.label;
      this.cursor = this.entries.length;
      return;
    }
    this.entries.push({
      label: change.label,
      before: new Map(change.before),
      after: new Map(change.after),
      mergeKey: change.mergeKey,
    });
    if (this.entries.length > this.capacity) this.entries.splice(0, this.entries.length - this.capacity);
    this.cursor = this.entries.length;
  }

  undo(): LevelChange | undefined {
    if (!this.canUndo) return undefined;
    this.cursor -= 1;
    return this.entries[this.cursor];
  }

  redo(): LevelChange | undefined {
    if (!this.canRedo) return undefined;
    const change = this.entries[this.cursor];
    this.cursor += 1;
    return change;
  }

  clear(): void {
    this.entries = [];
    this.cursor = 0;
  }
}
